<template>
  <div class="reference-panel-container" v-element-visibility="onElementVisibility">
    <ExpansionPanel
      v-show="hasCitedReferences"
      v-model="isExpanded"
      title="References"
      icon-name="slides"
      :collectionId="data.collectionId"
      :collectionName="data.collectionName"
      :count="String(data.citedReferences.length)"
    >
      <ReferenceCard
        v-for="(reference, index) in data.citedReferences"
        :key="`${reference.externalId}-${index}`"
        :index="index + 1"
        :reference="reference"
        :collectionId="data.collectionId"
        :collectionName="data.collectionName"
      />
    </ExpansionPanel>

    <ExpansionPanel
      v-show="hasRelatedResources"
      v-model="isRelatedExpanded"
      icon-name="slides"
      :title="'Related Resources'"
      :collectionId="data.collectionId"
      :collectionName="data.collectionName"
      :count="String(data.references.length)"
    >
      <ReferenceCard
        v-for="(reference, index) in data.references"
        :key="`${reference.externalId}-${index}`"
        :index="index + 1"
        :reference="reference"
        :collectionId="data.collectionId"
        :collectionName="data.collectionName"
      />
    </ExpansionPanel>
  </div>
</template>

<script>
import { computed, defineComponent, ref, watch } from 'vue';
import { vElementVisibility as elementVisibility } from '@vueuse/components';
import ReferenceCard from '~/components/conversation/referencePanel/ReferenceCard';
import useDocuments from '~/composables/useDocuments';
import ExpansionPanel from '~/components/common/ExpansionPanel';

export default defineComponent({
  components: { ExpansionPanel, ReferenceCard },
  directives: { elementVisibility },
  props: {
    messageId: {
      type: String,
    },
    data: {
      type: Object,
      default: () => {},
    },
  },
  setup(props) {
    const { setMessageReferencesThumbs } = useDocuments();

    const isVisible = ref(false);
    const isExpanded = ref(false);
    const isRelatedExpanded = ref(false);

    const hasCitedReferences = computed(() => props.data.citedReferences?.length > 0);
    const hasRelatedResources = computed(() => props.data.references?.length > 0);

    const onElementVisibility = (state) => {
      isVisible.value = state;
    };

    watch(isExpanded, (val) => {
      if (val) {
        refreshThumbsData();
      }
    });

    watch(isRelatedExpanded, (val) => {
      if (val) {
        refreshThumbsData();
      }
    });

    const refreshThumbsData = () => {
      setMessageReferencesThumbs(props.messageId, props.data);
    };

    return {
      onElementVisibility,
      hasCitedReferences,
      hasRelatedResources,
      isExpanded,
      isRelatedExpanded,
      refreshThumbsData,
    };
  },
});
</script>

<style scoped lang="scss">
.reference-panel-container {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;

  :deep(.v-expansion-panel--active) {
    .v-expansion-panel-title {
      min-height: 28px !important;
    }
  }

  :deep(.v-expansion-panel) {
    background-color: $bb-neutral-6;
    border-radius: 12px;
    padding: $bb-spacing-2 $bb-spacing-3 $bb-spacing-2 $bb-spacing-4;

    .v-expansion-panel__shadow {
      box-shadow: none;
    }

    .v-expansion-panel-title {
      padding: 0;
      min-height: 0;

      .v-expansion-panel-title__overlay {
        background-color: $bb-neutral-6;
      }
    }

    .v-expansion-panel-text {
      .v-expansion-panel-text__wrapper {
        padding: $bb-spacing-2 0;
        display: flex;
        flex-direction: column;
        gap: $bb-spacing-2;

        & > .document-card-wrapper:last-child {
          .document-details {
            border-bottom: none;
          }
        }

        & > div:last-of-type {
          border: none;

          .divider {
            display: none;
          }
        }
      }
    }
  }

  .toggle-arrow {
    cursor: pointer;
  }
}
</style>
